//React
import { FunctionComponent, useEffect, useRef } from "react";

//App Hooks
import {
	useAppDispatch,
	useAppSelector,
} from "../../../system/redux/hooks/redux-store-hooks";

//Selectors

import { selectTotalPages } from "../../redux/selectors/pagination/select-total-pages";
import { selectMatchedResults } from "../../redux/selectors/facets/select-matched-results";
import { selectCurrentMatchedPage } from "../../redux/selectors/pagination/select-current-matched-page";

//Actions
import { setCurrentMatchedPage } from "../../redux/slices/result-list-slice";

//Util
import { getPageList } from "../../functions/pagination/get-page-list";
import { getPageClassName } from "../../functions/pagination/get-page-class-name";
import { getPagerSummary } from "../../functions/pagination/get-pager-summary";
import { selectIsMobile } from "../../../system/redux/selectors/select-is-mobile";

/** Outputs the pagination links for the currently selected results that match the current facet selection. */
const ResultListPager: FunctionComponent = (props) => {
	//Store Dispatch
	const dispatch = useAppDispatch();

	//State Values
	const currentPage = useAppSelector(selectCurrentMatchedPage);
	const totalPages = useAppSelector(selectTotalPages);
	const isMobile = useAppSelector(selectIsMobile);

	//Use Ref
	const firstUpdate = useRef(true);

	//Use Effects
	//When the page changes scroll to the top on desktop and to below the map on mobile/tablet
	useEffect(() => {
		//Don't scroll on the initial load
		if (firstUpdate.current === true) {
			firstUpdate.current = false;
			return;
		}

		if (!isMobile) {
			window.scrollTo(0, 0);
		} else {
			let anchorElement = document.getElementById("area-info-anchor");

			if (anchorElement) {
				scrollIntoViewWithOffset(anchorElement, 100);
			}
		}
	}, [currentPage]);

	//Functions
	const scrollIntoViewWithOffset = (element: HTMLElement, offset: number) => {
		if (element) {
			const elementRect = element.getBoundingClientRect();
			const currentScrollPosition =
				window.pageYOffset || document.documentElement.scrollTop;
			const targetScrollPosition =
				elementRect.top + currentScrollPosition - offset;

			window.scrollTo({
				top: targetScrollPosition,
				behavior: "smooth", // Optional: for smooth scrolling
			});
		}
	};

	const goToNextPage = () => {
		if (currentPage !== totalPages) {
			dispatch(setCurrentMatchedPage(currentPage + 1));
		}
	};

	const goToPreviousPage = () => {
		if (currentPage !== 1) {
			dispatch(setCurrentMatchedPage(currentPage - 1));
		}
	};

	const goToPage = (page: number) => {
		if (page < 1) {
			page = 1;
		}

		if (page > totalPages) {
			page = totalPages;
		}

		dispatch(setCurrentMatchedPage(page));
	};

	const hasNextPage = () => {
		if (currentPage === totalPages) {
			return false;
		}

		return true;
	};

	const hasPreviousPage = () => {
		if (currentPage === 1) {
			return false;
		}

		return true;
	};

	//Do not show the pager if there are 1 or less pages.
	if (totalPages <= 1) {
		return null;
	}

	//JSX
	return (
		<div className="pager">
			<nav>
				{hasPreviousPage() && (
					<button
						className="pagination__button pagination__button--next-previous"
						type="button"
						id="btn-back"
						disabled={!hasPreviousPage()}
						onClick={() => goToPreviousPage()}
					>
						<span className="pagination-icon">
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12">
								<path
									d="M5.2.3.3 5.2c-.4.4-.4 1.1 0 1.6l4.9 4.9c.3.3.8.3 1.1 0 .3-.3.3-.8 0-1.1L2.6 6.8h8.6c.4 0 .8-.3.8-.8 0-.4-.4-.8-.8-.8H2.6l3.7-3.7c.3-.3.3-.8 0-1.1C6 0 5.5 0 5.2.3z"
									fill="currentColor"
								></path>
							</svg>
						</span>
						Back
					</button>
				)}

				{getPageList(currentPage, totalPages).map((page) => (
					<button
						className={getPageClassName(
							page,
							currentPage,
							"pagination__button",
						)}
						key={page}
						disabled={page === currentPage}
						onClick={() => goToPage(page)}
					>
						{page}
					</button>
				))}
				{hasNextPage() && (
					<button
						className="pagination__button pagination__button--next-previous "
						type="button"
						id="btn-next"
						disabled={!hasNextPage()}
						onClick={() => goToNextPage()}
					>
						Next
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12">
							<path
								d="M5.7.3c-.3.3-.3.8 0 1.1l3.7 3.7H.8c-.4.1-.8.4-.8.9 0 .4.4.8.8.8h8.6l-3.7 3.7c-.3.3-.3.8 0 1.1.3.4.8.4 1.1.1l4.9-4.9c.4-.4.4-1.1 0-1.6L6.8.3C6.5 0 6 0 5.7.3z"
								fill="currentColor"
							></path>
						</svg>
					</button>
				)}
			</nav>
		</div>
	);
};

export default ResultListPager;
